import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, InlineStack, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import { fetchFinanceDetailRefreshAsync } from "containers/finance-detail/finance-detail-container.slice";
import { createFinanceForYear } from "services/customers.service.typed";
import { regenerateFinanceDocument } from "services/customers.service";

interface PostData {
  invoice_deferral_days: number;
  debt_limit: number;
}

interface ConfirmModalProps {
  companyId: string;
  customerId: string;
  formValues: PostData;
  year?: number;
  financeId?: string;
  /** Company director saving is the approval itself (backend approves in the same request). */
  selfApprove?: boolean;
  /** Same computation as the manager-approval popup: an already-generated, unsigned document needs rebuilding with the new terms. */
  shouldRegenerateDocument?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  companyId,
  customerId,
  formValues,
  year,
  financeId,
  selfApprove,
  shouldRegenerateDocument,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      setErrMsg(null);
      // Use service function to ensure year is included in the request
      // The backend only self-approves when the request explicitly asks for
      // it — a qualifying director alone is not enough (old bundles and the
      // plain Save path must never trigger it, since they never regenerate
      // the document either).
      let result: any;
      if (year) {
        const requestData = selfApprove ? { ...formValues, selfApprove: true } : formValues;
        result = await createFinanceForYear(
          companyId,
          customerId,
          year,
          requestData,
          financeId ?? undefined
        );
      } else {
        setIsLoading(false);
        return;
      }

      // isEdit: silent background refetch — keeps the finance page's shared
      // customer data visible instead of flipping it to the blocking loader.
      dispatch(fetchCustomer({ customerId, companyId, isEdit: true }));

      // Backend self-approves in the same request for a director with approval
      // right. The approval popup's own regeneration step then applies here
      // too: an already generated but unsigned document still carries the old
      // terms and must be rebuilt, or the self-approval would silently ship
      // stale terms.
      const isSelfApproved = result?.selfApproved === true;
      let regenerateFailed = false;
      if (isSelfApproved && shouldRegenerateDocument && year) {
        try {
          await regenerateFinanceDocument(
            companyId,
            customerId,
            year,
            financeId ?? undefined
          );
        } catch (regenerateError) {
          regenerateFailed = true;
        }
      }

      // Refresh finance detail if year is provided (pass financeId to stay on same revision)
      if (year) {
        dispatch(fetchFinanceDetailRefreshAsync({
          companyId,
          customerId,
          year,
          financeId,
        }));
      }

      setIsLoading(false);
      closeModal();

      if (isSelfApproved) {
        if (regenerateFailed) {
          toast.error(t("finance_document_regenerate_failed"));
        } else {
          toast.success(t("finance_terms_approved"));
        }
      } else {
        toast.success(t("request_sent_to_manager_for_approval"));
      }
    } catch (e: any) {
      setErrMsg("error");
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  return (
    <>
      <div className="form-warnings">
        {errMsg && (
          <div>
            <Banner tone="critical">{t(errMsg)}</Banner>
          </div>
        )}
      </div>
      <h1 style={{ textAlign: "center" }}>
        {selfApprove
          ? t("are_you_sure_you_want_to_save_and_approve_finance")
          : t(
              "are_you_sure_you_want_to_save_finance_approval_and_notify_manager"
            ) ||
            "Are you sure you want to save finance approval and notify manager?"}
      </h1>
      <InlineStack align="space-between">
        <Button onClick={handleSave} loading={isLoading}>
          {t("Yes")}
        </Button>

        <Button onClick={closeModal}>{t("No")}</Button>
      </InlineStack>
    </>
  );
};

export default ConfirmModal;
